import { Box, Button, Typography } from "@material-ui/core";
import React, { useMemo } from "react";
import {
  CheckInInstructions,
  getCancellationPolicyInfo,
  getCancellationPolicyInfoV2,
  HotelNeedToKnowPanel,
  HotelSummaryPanel,
  Icon,
  IconName,
  useDeviceTypes,
  SanitizedHtml,
} from "halifax";
import clsx from "clsx";
import { RouteComponentProps } from "react-router-dom";
import { isCorpTenant, isVesperTenant, getConfigTenant } from "@capone/common";
import {
  CancellationPolicyEnum,
  CancellationReason,
  PACKAGES_REVIEW_PAGE_CHANGE_HOTEL_SELECTED,
  CHANGE_ROOM_SELECTED,
  CheckInPolicy,
  CorpRoomProduct,
  HotelCancellationPolicyV2Enum,
} from "redmond";

import {
  formattedCheckIn,
  formattedCheckOut,
  NEED_TO_KNOW_DETAILS,
  NEED_TO_KNOW_TITLE,
  CHECK_IN_INSTRUCTIONS_TITLE,
  CHANGE_HOTEL,
  CHANGE_ROOM,
  CALIFORNIA_BILL_644_CANCELLATION_TEXT,
} from "./constants";
import { ReviewHotelItineraryConnectorProps } from "./container";
import { config } from "../../../../api/config";
import "./styles.scss";
import { trackEvent } from "../../../../api/v0/trackEvent";
import {
  HOTELS_CANCELLATION_POLICY_EXPERIMENT,
  useExperiments,
  useIsPackagesShoppingApiV2Enabled,
  XMOD_V1_EXPERIMENT,
} from "../../../../context/experiments";
import { AVAILABLE, getExperimentVariant } from "@capone/experiments";

interface IReviewHotelItineraryProps
  extends ReviewHotelItineraryConnectorProps,
    RouteComponentProps {
  setOpenChangeHotelModal?: (arg: boolean) => void;
  setOpenChangeRoomModal?: (arg: boolean) => void;
  showCardContentIcons?: boolean;
  showSimplifiedCancellationPolicy?: boolean;
  adultsCount?: number;
  childrenAges?: number[];
}

export const ReviewHotelItinerary = ({
  selectedLodging,
  checkIn,
  checkOut,
  chosenProduct,
  selectedPackageByLodging,
  setOpenChangeHotelModal,
  setOpenChangeRoomModal,
  showCardContentIcons = true,
  roomInfo,
  cancellationSummary,
  cancellationPolicyV2,
  showSimplifiedCancellationPolicy,
  adultsCount,
  childrenAges,
  packagedRoomComponentV2,
}: IReviewHotelItineraryProps) => {
  const { matchesMobile } = useDeviceTypes();
  const isVesper = isVesperTenant(getConfigTenant());

  const expState = useExperiments();
  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const isPackagesShoppingApiV2Enabled = useIsPackagesShoppingApiV2Enabled();

  // Hooks must run unconditionally on every render — under v2,
  // packagedRoomComponentV2 starts null and later flips to non-null once
  // pollPackageVersion settles, within this same mounted instance. If this
  // useMemo were below the early returns, that flip would change the number
  // of hooks called between renders ("Rendered more hooks than during the
  // previous render").
  const mergedPolicies = useMemo(
    () =>
      [...(chosenProduct?.policies ?? [])].reduce(
        (uniquePolicies, currentPolicy) => {
          if (
            !uniquePolicies.some(({ title }) => currentPolicy.title === title)
          ) {
            uniquePolicies.push(currentPolicy);
          }
          return uniquePolicies;
        },
        [] as CheckInPolicy[]
      ),
    [chosenProduct?.policies]
  );

  if (isPackagesShoppingApiV2Enabled) {
    if (!packagedRoomComponentV2) return null;
  } else if (!selectedPackageByLodging) {
    return null;
  }

  const cancellationPolicyExperimentEnabled =
    getExperimentVariant(
      expState.experiments,
      HOTELS_CANCELLATION_POLICY_EXPERIMENT
    ) === AVAILABLE;
  const cancellationPolicy = chosenProduct?.cancellationPolicy
    ? getCancellationPolicyInfo(chosenProduct?.cancellationPolicy)
    : null;
  const cancellationPolicyV2Info = cancellationPolicyV2
    ? getCancellationPolicyInfoV2(cancellationPolicyV2)
    : null;
  const cancellationPolicyInfo =
    cancellationPolicyExperimentEnabled || isPackagesShoppingApiV2Enabled
      ? cancellationPolicyV2Info
      : cancellationPolicy;

  const isRefundable = () => {
    if (cancellationPolicyExperimentEnabled || isPackagesShoppingApiV2Enabled) {
      return (
        cancellationPolicyV2?.CancellationPolicyV2 ===
          HotelCancellationPolicyV2Enum.FullyRefundable ||
        cancellationPolicyV2?.CancellationPolicyV2 ===
          HotelCancellationPolicyV2Enum.PartiallyRefundable
      );
    } else {
      return (
        chosenProduct?.cancellationPolicy.CancellationPolicy ===
        CancellationPolicyEnum.Refundable
      );
    }
  };
  const isNonRefundable = () => {
    if (cancellationPolicyExperimentEnabled || isPackagesShoppingApiV2Enabled) {
      return (
        cancellationPolicyV2?.CancellationPolicyV2 ===
        HotelCancellationPolicyV2Enum.NonRefundable
      );
    } else {
      return (
        chosenProduct?.cancellationPolicy.CancellationPolicy ===
        CancellationPolicyEnum.NonRefundable
      );
    }
  };

  return (
    <Box
      className={clsx("review-pkg-hotel-itinerary", {
        mobile: matchesMobile,
        xmod: isXmodRedesign,
        redesigned: isVesper,
      })}
    >
      {cancellationPolicyInfo &&
      showSimplifiedCancellationPolicy &&
      !isNonRefundable() ? (
        <div className="free-cancellation-policy-banner">
          <Icon name={IconName.CheckCircleFilled} />
          <Typography className="free-cancellation-policy-banner-text">
            {cancellationPolicyInfo.primaryText}
          </Typography>
        </div>
      ) : null}
      <Box className="review-itinerary-card-content-container hotel-summary-panel-section">
        <HotelSummaryPanel
          selectedLodging={selectedLodging ?? null}
          checkIn={checkIn || null}
          checkOut={checkOut || null}
          isMobile={matchesMobile}
          showCollectionBanner={false}
          roomInfo={roomInfo?.roomInfo}
          showRoomCapacityInfo
          {...(isCorpTenant(config.TENANT) && {
            chosenProduct: chosenProduct as CorpRoomProduct,
          })}
          adultsCount={adultsCount}
          childrenAges={childrenAges}
        />
        {!!setOpenChangeHotelModal || !!setOpenChangeRoomModal ? (
          <Box className="change-ctas-container">
            {!!setOpenChangeHotelModal && (
              <Button
                onClick={() => {
                  setOpenChangeHotelModal(true);
                  trackEvent({
                    eventName: PACKAGES_REVIEW_PAGE_CHANGE_HOTEL_SELECTED,
                    properties: {},
                    encryptedProperties: [],
                  });
                }}
                className="change-cta"
              >
                <Typography className="change-cta-text">
                  {CHANGE_HOTEL}
                </Typography>
              </Button>
            )}

            {!!setOpenChangeRoomModal && (
              <Button
                onClick={() => {
                  setOpenChangeRoomModal(true);
                  trackEvent({
                    eventName: CHANGE_ROOM_SELECTED,
                    properties: {},
                    encryptedProperties: [],
                  });
                }}
                className="change-cta"
              >
                <Typography className="change-cta-text">
                  {CHANGE_ROOM}
                </Typography>
              </Button>
            )}
          </Box>
        ) : null}
      </Box>

      {cancellationPolicyInfo &&
      showSimplifiedCancellationPolicy &&
      isNonRefundable() ? (
        <Box
          className={clsx(
            "review-itinerary-card-content-container",
            "cancellation-details"
          )}
        >
          <Box className="text-container">
            <Typography
              variant="h4"
              component="h2"
              className={clsx("section-header")}
            >
              {cancellationPolicyInfo.primaryText}
            </Typography>
            <Typography variant="body2">
              {cancellationSummary?.reasons.includes(
                CancellationReason.CaliforniaBill644
              )
                ? CALIFORNIA_BILL_644_CANCELLATION_TEXT
                : cancellationPolicyInfo.secondaryText}
            </Typography>
          </Box>
        </Box>
      ) : null}

      {selectedLodging?.lodging?.checkInInstructions &&
        checkIn &&
        checkOut && (
        <Box
          className={clsx(
            "review-itinerary-card-content-container",
            "check-in-schedule"
          )}
        >
          {showCardContentIcons && <Icon name={IconName.CheckInIcon} />}
          <Box className="text-container">
            <Typography
              variant="h4"
              component="h2"
              className="hotel-shop-check-in-schedule-heading section-header"
            >
              {CHECK_IN_INSTRUCTIONS_TITLE}
            </Typography>
            <SanitizedHtml
              as={Typography}
              variant="body2"
              html={formattedCheckIn(
                checkIn,
                selectedLodging.lodging.checkInInstructions
                  ?.checkInTime
              )}
            />
            <SanitizedHtml
              as={Typography}
              variant="body2"
              html={formattedCheckOut(
                checkOut,
                selectedLodging.lodging.checkInInstructions
                  ?.checkOutTime
              )}
            />
          </Box>
        </Box>
      )}
      {selectedLodging?.lodging?.checkInInstructions &&
      selectedLodging?.lodging?.checkInInstructions?.policies
        .length ? (
        <Box
          className={clsx(
            "review-itinerary-card-content-container",
            "check-in-instructions"
          )}
        >
          <CheckInInstructions
            policies={
              selectedLodging?.lodging?.checkInInstructions
                ?.policies
            }
          />
        </Box>
      ) : undefined}

      {cancellationPolicyInfo && !showSimplifiedCancellationPolicy ? (
        <Box
          className={clsx(
            "review-itinerary-card-content-container",
            "cancellation-details"
          )}
        >
          {showCardContentIcons && <Icon name={IconName.ErrorState} />}

          <Box className="text-container">
            <Typography
              variant={matchesMobile ? "h4" : "h2"}
              component="h2"
              className={clsx("section-header", {
                green: isRefundable(),
              })}
            >
              {cancellationPolicyInfo.primaryText}
            </Typography>
            <Typography variant="body2">
              {isNonRefundable() &&
              cancellationSummary?.reasons.includes(
                CancellationReason.CaliforniaBill644
              )
                ? CALIFORNIA_BILL_644_CANCELLATION_TEXT
                : cancellationPolicyInfo.secondaryText}
            </Typography>
          </Box>
        </Box>
      ) : null}

      <HotelNeedToKnowPanel
        title={NEED_TO_KNOW_TITLE}
        subtitle={NEED_TO_KNOW_DETAILS}
        policies={mergedPolicies}
        fetchingPriceQuote={false}
      />
    </Box>
  );
};
